// ---------------------------------------------------------------------------
// TelemetryPanel — declarative panel-scoped telemetry boundary.
//
// Rendering this component:
//   1. fires a userbi `recordView` exactly once, on first render, and
//   2. publishes { panelType, panelRegion } plus panel-bound recorders
//      (recordPanelAction / recordPanelView) via TelemetryContext so
//      descendants can emit panel-scoped events through useTelemetryContext().
//
// The once-only guarantee uses a ref guard so it survives React 18 StrictMode's
// intentional double-invocation of effects (and any later re-render).
// ---------------------------------------------------------------------------

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useUserBi } from '../../hooks/useUserBi/useUserBi.js';

import type {
  PanelActionOptions,
  PanelViewOptions,
  TelemetryContextValue,
  TelemetryPanelProps,
} from './TelemetryPanel.interface.js';

export const TelemetryContext = createContext<TelemetryContextValue | null>(
  null,
);

export function useTelemetryContext(): TelemetryContextValue {
  const ctx = useContext(TelemetryContext);
  if (!ctx) {
    throw new Error(
      'useTelemetryContext must be used within a <TelemetryPanel>',
    );
  }
  return ctx;
}

export function TelemetryPanel({
  children,
  as: Wrapper = Fragment,
  panelType,
  panelRegion,
  eventID,
  moduleName,
  moduleType,
  data: extra,
}: TelemetryPanelProps) {
  const { recordAction, recordView } = useUserBi();

  const recordPanelAction = useCallback(
    (options: PanelActionOptions) => {
      recordAction({ panelType, panelRegion, ...options });
    },
    [recordAction, panelType, panelRegion],
  );

  const recordPanelView = useCallback(
    (options: PanelViewOptions) => {
      recordView({ panelType, panelRegion, ...options });
    },
    [recordView, panelType, panelRegion],
  );

  const viewFired = useRef(false);
  useEffect(() => {
    if (viewFired.current) return;
    viewFired.current = true;
    recordView({
      panelType,
      panelRegion,
      eventID,
      moduleName,
      moduleType,
      data: extra,
    });
    // Fire once on mount only — the panel's identity is fixed for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<TelemetryContextValue>(
    () => ({ panelType, panelRegion, recordPanelAction, recordPanelView }),
    [panelType, panelRegion, recordPanelAction, recordPanelView],
  );

  return (
    <TelemetryContext.Provider value={value}>
      <Wrapper>{children}</Wrapper>
    </TelemetryContext.Provider>
  );
}
